import { dashboardNavGroups, type DashboardNavItemDef } from './dashboardNav'

/**
 * Saved Home layouts (z8nrz7ep6x): named, ordered sets of Home tiles a user
 * builds for themselves ("Cleaning", "Inventory"). Stored server-side as one
 * `UserPreference` per tenant (see `homeLayoutsKey`), so they follow the person
 * between devices and each tenant has its own.
 *
 * `active: null` is the default Home, the three columns. It is not a stored
 * layout and cannot be edited; customizing it creates a new layout.
 */
const HOME_LAYOUTS_KEY_PREFIX = 'home.layouts'

/**
 * The preference key for one tenant's layouts.
 *
 * The tenant rides in the key because `UserPreference` has no tenant column;
 * adding one would change every preference (table columns, the board) for a
 * need only this one has. Tenant ids fit the backend's key pattern (letters,
 * digits, `._:-`), so the key is safe as a URL segment as it is.
 */
export function homeLayoutsKey(tenantId: string): string {
  return `${HOME_LAYOUTS_KEY_PREFIX}:${tenantId}`
}

/** Keeps the stored value far below the backend's 32 KB per-key cap. */
export const MAX_HOME_LAYOUTS = 20
export const MAX_LAYOUT_NAME_LENGTH = 40
const MAX_LAYOUT_ID_LENGTH = 64

export interface HomeLayout {
  id: string
  name: string
  /** Tile ids in display order. Only tiles on Home; a tile left out is hidden. */
  tiles: string[]
}

export interface HomeLayoutPrefs {
  active: string | null
  layouts: HomeLayout[]
}

export const DEFAULT_HOME_LAYOUT_PREFS: HomeLayoutPrefs = { active: null, layouts: [] }

export type HomeTile = DashboardNavItemDef & { group: string }

/** Every Home tile, in column order. */
export const HOME_TILES: HomeTile[] = dashboardNavGroups.flatMap((group) =>
  group.items.map((item) => ({ ...item, group: group.title })),
)

const TILE_BY_ID = new Map(HOME_TILES.map((tile) => [tile.id, tile]))

export function homeTile(id: string): HomeTile | undefined {
  return TILE_BY_ID.get(id)
}

/**
 * Whether the signed-in user may see a tile at all. Permission HIDES (a feature
 * flag only greys, and is applied separately). `canAnyRoute` is the caller's
 * `canAny(navPermissionsFor(to))`, passed in so this module stays pure.
 */
export function isTileAllowed(
  tile: Pick<DashboardNavItemDef, 'to' | 'superAdminOnly'>,
  access: { isSuperAdmin: boolean; canAnyRoute: (to: string) => boolean },
): boolean {
  return tile.superAdminOnly ? access.isSuperAdmin : access.canAnyRoute(tile.to)
}

/**
 * The default Home as one list, read row by row across the columns.
 *
 * A custom layout renders as one three-wide grid, so reading the columns row by
 * row puts every tile where it already stood: customizing starts from the
 * screen the user is looking at, not from a reshuffle. Hidden tiles are skipped
 * per column first, because that is how the columns themselves close up.
 */
export function defaultTileOrder(isAllowed: (id: string) => boolean = () => true): string[] {
  const columns = dashboardNavGroups.map((group) =>
    group.items.filter((item) => isAllowed(item.id)).map((item) => item.id),
  )
  const rows = Math.max(0, ...columns.map((column) => column.length))
  const order: string[] = []
  for (let row = 0; row < rows; row++) {
    for (const column of columns) {
      if (row < column.length) order.push(column[row])
    }
  }
  return order
}

function knownTileIds(raw: unknown): string[] {
  if (!Array.isArray(raw)) return []
  const seen = new Set<string>()
  for (const id of raw) {
    if (typeof id === 'string' && TILE_BY_ID.has(id)) seen.add(id)
  }
  return [...seen]
}

/**
 * Whatever is stored, made into valid prefs.
 *
 * A stored value is untrusted: an older shape, a hand-edited row, or a value
 * from a later version must still render a working Home. Unknown tiles (one
 * removed from the catalog) are dropped, duplicate layouts and ids collapse to
 * the first, and an active id with no layout falls back to the default Home.
 */
export function coerceHomeLayoutPrefs(raw: unknown): HomeLayoutPrefs {
  const obj = typeof raw === 'object' && raw !== null ? (raw as Record<string, unknown>) : {}
  const layouts: HomeLayout[] = []
  const ids = new Set<string>()
  for (const entry of Array.isArray(obj.layouts) ? obj.layouts : []) {
    if (layouts.length >= MAX_HOME_LAYOUTS) break
    if (typeof entry !== 'object' || entry === null) continue
    const { id, name, tiles } = entry as Record<string, unknown>
    if (typeof id !== 'string' || id.length === 0 || id.length > MAX_LAYOUT_ID_LENGTH) continue
    if (ids.has(id)) continue
    if (typeof name !== 'string') continue
    const trimmed = name.trim().slice(0, MAX_LAYOUT_NAME_LENGTH)
    if (!trimmed) continue
    ids.add(id)
    layouts.push({ id, name: trimmed, tiles: knownTileIds(tiles) })
  }
  const active = typeof obj.active === 'string' && ids.has(obj.active) ? obj.active : null
  return { active, layouts }
}

export function isDefaultHomeLayoutPrefs(prefs: HomeLayoutPrefs): boolean {
  return prefs.active === null && prefs.layouts.length === 0
}

export function activeHomeLayout(prefs: HomeLayoutPrefs): HomeLayout | null {
  return prefs.layouts.find((layout) => layout.id === prefs.active) ?? null
}

/** A user-facing problem with a layout name, or null when it can be saved. */
export function validateLayoutName(
  prefs: HomeLayoutPrefs,
  name: string,
  exceptId?: string,
): string | null {
  const trimmed = name.trim()
  if (!trimmed) return 'Enter a name.'
  if (trimmed.length > MAX_LAYOUT_NAME_LENGTH) {
    return `Use ${MAX_LAYOUT_NAME_LENGTH} characters or fewer.`
  }
  const lower = trimmed.toLowerCase()
  if (prefs.layouts.some((l) => l.id !== exceptId && l.name.toLowerCase() === lower)) {
    return 'You already have a layout with this name.'
  }
  return null
}

export function canAddHomeLayout(prefs: HomeLayoutPrefs): boolean {
  return prefs.layouts.length < MAX_HOME_LAYOUTS
}

/** Adds a layout and makes it the active one. Past the cap, nothing changes. */
export function addHomeLayout(prefs: HomeLayoutPrefs, layout: HomeLayout): HomeLayoutPrefs {
  if (!canAddHomeLayout(prefs)) return prefs
  return coerceHomeLayoutPrefs({ active: layout.id, layouts: [...prefs.layouts, layout] })
}

/** Replaces a layout's tiles and makes it the active one. */
export function saveHomeLayoutTiles(
  prefs: HomeLayoutPrefs,
  id: string,
  tiles: string[],
): HomeLayoutPrefs {
  if (!prefs.layouts.some((l) => l.id === id)) return prefs
  return coerceHomeLayoutPrefs({
    active: id,
    layouts: prefs.layouts.map((l) => (l.id === id ? { ...l, tiles } : l)),
  })
}

/** Removes a layout. Deleting the active one returns Home to the default. */
export function deleteHomeLayout(prefs: HomeLayoutPrefs, id: string): HomeLayoutPrefs {
  return {
    active: prefs.active === id ? null : prefs.active,
    layouts: prefs.layouts.filter((l) => l.id !== id),
  }
}

/** Switches Home to a layout, or to the default with null. */
export function selectHomeLayout(prefs: HomeLayoutPrefs, id: string | null): HomeLayoutPrefs {
  const active = id !== null && prefs.layouts.some((l) => l.id === id) ? id : null
  return { ...prefs, active }
}

/**
 * The tiles to store after an edit.
 *
 * The editor only shows tiles the user may see now. A tile the layout held
 * that is hidden now (a role change took the permission away) is kept, at the
 * end, so it comes back by itself if the permission does.
 */
export function mergeHiddenTiles(
  draft: string[],
  previous: string[],
  isAllowed: (id: string) => boolean,
): string[] {
  const kept = [...new Set(draft)]
  const inDraft = new Set(kept)
  return [...kept, ...previous.filter((id) => !isAllowed(id) && !inDraft.has(id))]
}

/** Moves the tile at `from` to `to`. Same array back for a move that is not one. */
export function moveTile(ids: string[], from: number, to: number): string[] {
  if (from === to) return ids
  if (from < 0 || to < 0 || from >= ids.length || to >= ids.length) return ids
  const next = [...ids]
  const [moved] = next.splice(from, 1)
  next.splice(to, 0, moved)
  return next
}

/** Puts a tile on Home (at the end) or takes it off. */
export function setTileShown(ids: string[], id: string, shown: boolean): string[] {
  const has = ids.includes(id)
  if (shown) return has ? ids : [...ids, id]
  return has ? ids.filter((x) => x !== id) : ids
}

export const CHOOSE_APPS_FILTERS = ['All', ...dashboardNavGroups.map((g) => g.title)] as const

export type ChooseAppsRow = { tile: HomeTile; shown: boolean; position: number }

/**
 * The rows of the Choose apps list: tiles on Home first, in their Home order
 * (so the up/down buttons read as moving them on Home), then the rest in
 * catalog order. `position` is the index on Home, -1 when off.
 */
export function chooseAppsRows(
  draft: string[],
  allowed: HomeTile[],
  query: string,
  filter: string,
): ChooseAppsRow[] {
  const allowedIds = new Set(allowed.map((t) => t.id))
  const on: ChooseAppsRow[] = draft
    .filter((id) => allowedIds.has(id))
    .map((id) => ({ tile: homeTile(id)!, shown: true, position: draft.indexOf(id) }))
  const onIds = new Set(on.map((r) => r.tile.id))
  const off: ChooseAppsRow[] = allowed
    .filter((t) => !onIds.has(t.id))
    .map((tile) => ({ tile, shown: false, position: -1 }))
  const q = query.trim().toLowerCase()
  return [...on, ...off].filter(
    (r) =>
      (filter === 'All' || r.tile.group === filter) &&
      (!q || r.tile.label.toLowerCase().includes(q)),
  )
}
